/**
 * Way back from checkout to the destination landing page a visitor bought on.
 *
 * A landing page's Checkout button (`/plans/{loc}/?product=ID`, plus
 * `&checkout=1` on the newer pages) forwards straight to checkout, and neither
 * page's own Back button knew where the visitor started: checkout's pushed a
 * fresh plans entry and the plans page's went to "/", so /best-esim-for-egypt/
 * was unreachable without typing it again. gatsby-browser's onPreRouteUpdate
 * feeds every route change in here; the plans page asks for the landing page,
 * checkout for the previous route.
 *
 * Deliberately not the 30-day landing-page attribution cookie
 * (landing-page-tracking.ts): Back must follow this visit, not last week's.
 */

import { detectLandingPage } from "@utilities/landing-page-tracking";

interface RouteLocation {
  pathname: string;
  search: string;
}

const RETURN_KEY = "bnesim_checkout_return";
// Unprefixed "/plans/{loc}/" or "/{lang}/plans/{loc}/" (not /sim-cards/plans/…).
const PLANS_PAGE = /^(?:\/[a-z]{2})?\/plans\/([^/]+)\/?$/;

// In memory only, so a full page load starts without a previous route.
let previousLocation: RouteLocation | null = null;

const plansLocationOf = (pathname: string): string | null =>
  PLANS_PAGE.exec(pathname)?.[1] ?? null;

const withoutLanguage = (pathname: string): string =>
  pathname.replace(/^\/[a-z]{2}(?=\/)/, "");

// Any eSIM plans, checkout or thank-you page (any location).
const isInPlansFlow = (pathname: string): boolean =>
  withoutLanguage(pathname).startsWith("/plans/");

/** Whether `pathname` is the eSIM plans page of `location` (any language). */
export const isPlansPageOf = (pathname: string, location: string): boolean =>
  plansLocationOf(pathname) === location;

/** The route the visitor came from in this page load; null after a full load. */
export const getPreviousLocation = (): RouteLocation | null => previousLocation;

/** The landing page to return to from `/plans/{location}/`; null for the normal flow. */
export const getCheckoutReturnPath = (location: string): string | null => {
  try {
    const raw = window.sessionStorage.getItem(RETURN_KEY);
    if (raw === null) return null;
    const value = JSON.parse(raw) as { location?: unknown; path?: unknown };
    return value.location === location && typeof value.path === "string"
      ? value.path
      : null;
  } catch {
    return null;
  }
};

/** Called from gatsby-browser's onPreRouteUpdate on every route change. */
export const recordRouteChange = (
  location: RouteLocation,
  prevLocation: RouteLocation | null | undefined,
): void => {
  previousLocation =
    prevLocation === null || prevLocation === undefined
      ? null
      : { pathname: prevLocation.pathname, search: prevLocation.search };
  if (previousLocation === null) return;

  const plansLocation = plansLocationOf(location.pathname);
  if (plansLocation === null) return;
  try {
    // Any arrival from a landing page: its Checkout button (Japan, China,
    // Europe and USA send no `checkout=1`) or its own plans link. Same page
    // list as the analytics; matches /de/… and /christian/… prefixes too.
    const landingPage = detectLandingPage(previousLocation.pathname);
    if (landingPage !== null) {
      window.sessionStorage.setItem(
        RETURN_KEY,
        JSON.stringify({ location: plansLocation, path: `/${landingPage}/` }),
      );
    } else if (!isInPlansFlow(previousLocation.pathname)) {
      // Reached from outside the plans pages (home, menu, …): its Back button
      // goes the normal way again. Moves between plans pages (a regional card,
      // checkout and back) keep it, but it only applies to its own location,
      // so /plans/ae/ → /plans/global/ → Back still returns to /plans/ae/.
      window.sessionStorage.removeItem(RETURN_KEY);
    }
  } catch {
    // Storage blocked: the Back buttons keep their normal behaviour.
  }
};
